import { Search } from "@/components/search/Search.tsx";
import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { usePerformanceReviewsHook } from "@/hooks/use-performance-reviews.hook.tsx";
import { HistoryTable } from "@/pages/employees/employee/tables/HistoryTable.tsx";
import { ScoreTag } from "@/pages/talent/performance-reviews/view/components/ScoreTag.tsx";
import { useSearchEmployeeReviewParticipantViews } from "@/queries/employees/use-employee-performance-review.query.ts";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import {
  ApiPerformanceReviewParticipantView,
  ApiPerformanceReviewParticipantViewFilter,
  ApiPerformanceReviewStatus,
} from "@/types/performance-review.types.ts";
import {
  DropdownMenu,
  DropdownMenuItem,
  LinkTo,
  ListItem,
  Stack,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@skailer-com/uikit";
import { ColumnDef, createColumnHelper, DisplayColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebounce } from "react-use";

export const MyFeedbackAboutMe = () => {
  const { t } = useTranslation();
  const { renderDate, renderTimeAgo } = useFormatter();
  const { getPerformanceReviewStatusTag } = usePerformanceReviewsHook();
  const { data: employeeMe } = useEmployeeMe();
  const [searchTerm, setSearchTerm] = useState("");

  const [reviewFilter, setReviewFilter] = useState<ApiPerformanceReviewParticipantViewFilter>({
    page_number: 1,
  });

  useDebounce(
    () => {
      setReviewFilter((currentFilter) => ({
        ...currentFilter,
        page_number: 1,
        text: searchTerm || undefined,
      }));
    },
    300,
    [searchTerm]
  );

  const {
    data: reviewParticipantViews,
    isLoading: isReviewsLoading,
    isFetching: isReviewsFetching,
  } = useSearchEmployeeReviewParticipantViews({
    employeeId: employeeMe?.id ?? 0,
    filter: reviewFilter,
    enabled: employeeMe?.id != undefined,
  });

  const reviewColumnHelper = createColumnHelper<ApiPerformanceReviewParticipantView>();
  const reviewColumns = useMemo<ColumnDef<ApiPerformanceReviewParticipantView>[]>(() => {
    const inner: DisplayColumnDef<ApiPerformanceReviewParticipantView>[] = [];

    inner.push(
      reviewColumnHelper.display({
        id: "performance_review",
        header: t("common:title"),
        cell: (cell) => (
          <LinkTo
            to={`/performance-reviews/${cell.row.original.review.id}/view/participants/${employeeMe?.id}`}
            variant={"secondary"}
          >
            <ListItem
              emoji={cell.row.original.review.emoji}
              title={cell.row.original.review.title}
              forceShowAvatar={true}
              truncate={true}
              behavior={"card"}
            />
          </LinkTo>
        ),
        ...ColumnSizes.wide,
      })
    );

    inner.push(
      reviewColumnHelper.display({
        id: "score",
        header: t("performance_review:score"),
        cell: (cell) => {
          if (
            cell.row.original.review.status === ApiPerformanceReviewStatus.completed &&
            !cell.row.original.scale_score.avg_scale_score
          ) {
            return (
              <Stack direction="horizontal">
                <Text className="font-semibold">-</Text>
              </Stack>
            );
          }
          return (
            <Stack direction={"horizontal"}>
              <ScoreTag score={cell.row.original.scale_score.avg_scale_score} isPending />
            </Stack>
          );
        },
        ...ColumnSizes.standard,
      })
    );

    inner.push(
      reviewColumnHelper.display({
        id: "cycle_status",
        header: t("performance_review:cycle_status"),
        cell: (cell) => (
          <Stack items={"start"}>
            {getPerformanceReviewStatusTag(cell.row.original.review.status)}
          </Stack>
        ),
        ...ColumnSizes.middle,
      })
    );

    inner.push(
      reviewColumnHelper.display({
        id: "date",
        header: t("common:date"),
        cell: (cell) => {
          if (!cell.row.original.review.date_completed_at) return "-";
          const date = new Date(cell.row.original.review.date_completed_at);
          return (
            <Tooltip>
              <TooltipTrigger>
                <Text className="text-secondary">{renderTimeAgo(date)}</Text>
              </TooltipTrigger>
              <TooltipContent>{renderDate(date)}</TooltipContent>
            </Tooltip>
          );
        },
        ...ColumnSizes.middle,
      })
    );

    inner.push(
      reviewColumnHelper.display({
        id: "actions",
        header: "",
        cell: (cell) => (
          <Stack className={"justify-end"} gap={"sm"} direction={"horizontal"}>
            <DropdownMenu>
              <DropdownMenuItem
                title={t("common:open_results")}
                target={"_blank"}
                to={`/performance-reviews/${cell.row.original.review.id}/view/participants/${employeeMe?.id}`}
              />
            </DropdownMenu>
          </Stack>
        ),
        ...ColumnSizes.tiny,
      })
    );

    return inner;
  }, [
    employeeMe?.id,
    getPerformanceReviewStatusTag,
    renderDate,
    renderTimeAgo,
    reviewColumnHelper,
    t,
  ]);

  return (
    <Stack>
      <Search
        value={searchTerm}
        onInput={(e) => setSearchTerm(e.currentTarget.value)}
        placeholder={t("common:search")}
      />

      <HistoryTable<ApiPerformanceReviewParticipantView>
        historyItems={reviewParticipantViews?.items}
        columns={reviewColumns}
        canViewHistory={true}
        defaultVisibleRecords={20}
        isLoading={isReviewsLoading}
        isFetching={isReviewsFetching}
        pagination={{
          metadata: reviewParticipantViews?.metadata,
          currentPage: reviewFilter.page_number ?? 1,
          onPageChange: (page) => {
            setReviewFilter((currentFilter) => ({ ...currentFilter, page_number: page }));
          },
        }}
        emptyStateTitle={t("performance_review:employee_performance_empty_state_title")}
        notFoundComponent={<EmptyStateNothingFound />}
      />
    </Stack>
  );
};
